// The tenant feature `map` (#169), as the portal learns it — the same shape as
// src/core/staticsFeedbackFeature.ts, ceremonyFeature.ts and cmsFeature.ts, one
// rule for every flag.
//
// The flag is server truth: with it off, every map root and the snapshot
// answer FEATURE_DISABLED (the snapshot a 404). The portal has no flag query of
// its own, so it learns the answer from the first map call it makes — the
// shell's probe (MainLayout → probeMapFeature, for every visitor: the reader
// works signed out), the map page itself, the team pages' pit links — and
// hides every map surface (the rail entry, "Show my pit on the map", the
// teams table's pit links, the CMS box) once it knows the feature is off.
// Unknown counts as on: the entries are first-frame hints like every rail
// hint, and the page says so in place if the server refuses.
import { reactive } from 'vue'
import type { Client } from '@urql/vue'
import { MapFeatureProbeDocument } from './mapFeature.operations'
import { hasErrorCode } from 'src/modules/cms/cmsRender'

export const mapFeature = reactive({
  /** The server answered FEATURE_DISABLED. */
  off: false,
  /** The probe ran (once per session). */
  probed: false,
})

/** Records what a map answer says about the flag. */
export function noteMapAnswer(error: unknown): void {
  if (hasErrorCode(error, 'FEATURE_DISABLED')) mapFeature.off = true
  else if (!error) mapFeature.off = false
}

/** Asks once per session whether the map service answers. */
export async function probeMapFeature(client: Client): Promise<void> {
  if (mapFeature.probed) return
  mapFeature.probed = true
  try {
    const r = await client.query(MapFeatureProbeDocument, {}).toPromise()
    if (hasErrorCode(r.error, 'FEATURE_DISABLED')) mapFeature.off = true
  } catch {
    // Unreachable is not "off": the pages will say what is wrong.
  }
}

/** Test seam. */
export function resetMapFeature(): void {
  mapFeature.off = false
  mapFeature.probed = false
}
